import * as dateFns from 'date-fns';
import { fMonth } from 'src/utils/format-time';
import { categoryMap } from 'src/utils/catmap';
import { isRetired } from './forecast';

export const periods = [
  { label: 'Monthly', value: 'month' },
  { label: 'Quarterly', value: 'quarter' },
  { label: 'Yearly', value: 'year' },
];

export const startOfRollupPeriod = (date, rollup) => {
  switch (rollup) {
    case 'month':
      return dateFns.startOfMonth(date);
    case 'quarter':
      return dateFns.startOfQuarter(date);
    case 'year':
      return dateFns.startOfYear(date);
    default:
      throw new Error(`Unsupported rollup: ${rollup}`);
  }
};

export const endOfRollupPeriod = (date, rollup) => {
  switch (rollup) {
    case 'month':
      return dateFns.endOfMonth(date);
    case 'quarter':
      return dateFns.endOfQuarter(date);
    case 'year':
      return dateFns.endOfYear(date);
    default:
      throw new Error(`Unsupported rollup: ${rollup}`);
  }
};

const segmentsForMonth = (start, end, period) => {
  const startDate = dateFns.startOfMonth(start);
  const endDate = dateFns.endOfMonth(end);
  const num = countSegmentsInDateRange(startDate, endDate, period);

  return [...Array(num)].map((_, i) => {
    const newDate = new Date(startDate);
    newDate.setMonth(startDate.getMonth() + i);

    return { label: fMonth(newDate), value: newDate };
  });
};

const segmentsForQuarter = (start, end, period) => {
  const startDate = startOfRollupPeriod(new Date(start), period); // Zero to the first day of the quarter
  const num = countSegmentsInDateRange(startDate, end, period);
  const segments = [];

  const currentDate = new Date(startDate);

  for (let i = 0; i < num; i++) {
    const date = new Date(currentDate.toISOString());
    const quarter =
      currentDate.getMonth() <= 2
        ? 'Q1'
        : currentDate.getMonth() <= 5
        ? 'Q2'
        : currentDate.getMonth() <= 8
        ? 'Q3'
        : 'Q4';

    segments.push({
      label: `${quarter} ${currentDate.getFullYear()}`,
      value: date,
    });

    // Determine the next quarter bounds
    currentDate.setMonth(currentDate.getMonth() + 3);
  }

  return segments;
};

const segmentsForYear = (start, end, period) => {
  const startDate = startOfRollupPeriod(new Date(start), period);
  const num = countSegmentsInDateRange(startDate, end, period);

  return [...Array(num)].map((_, i) => {
    const newDate = new Date(
      startDate.getFullYear() + i,
      startDate.getMonth(),
      startDate.getDate()
    );
    return { label: `${newDate.getFullYear()}`, value: newDate };
  });
};

export const dateRangeForTransactions = (transactions) => {
  const start = findStartDate(transactions);
  const end = findEndDate(transactions);
  return { start, end };
};

export const findStartDate = (objects) => {
  if (!Array.isArray(objects) || objects.length === 0) return null;

  let earliestDate = new Date(objects[0].date);

  for (let i = 1; i < objects.length; i++) {
    const currentDate = new Date(objects[i].date);
    if (currentDate < earliestDate) {
      earliestDate = currentDate;
    }
  }

  return earliestDate;
};

export const findEndDate = (objects) => {
  if (!Array.isArray(objects) || objects.length === 0) {
    return null; // Return null if the input is not an array or if it's an empty array
  }

  let latestDate = new Date(objects[0].date);

  for (let i = 1; i < objects.length; i++) {
    const currentDate = new Date(objects[i].date);
    if (currentDate > latestDate) {
      latestDate = currentDate;
    }
  }

  return latestDate;
};

export const datesForRange = (period, _start, _end, retirementInfo) => {
  const start = _start || new Date();
  const end = _end || new Date();

  let value = [];
  switch (period) {
    case 'month':
      value = segmentsForMonth(start, end, period);
      break;
    case 'quarter':
      value = segmentsForQuarter(start, end, period);
      break;
    case 'year':
      value = segmentsForYear(start, end, period);
      break;
    default:
      value = [];
      break;
  }

  const now = new Date();
  return value.map((s, index) => ({
    ...s,
    minWidth: 115,
    endDate: endOfRollupPeriod(s.value, period),
    current:
      startOfRollupPeriod(s.value, period).getTime() === startOfRollupPeriod(now, period).getTime(),
    future:
      startOfRollupPeriod(s.value, period).getTime() >= startOfRollupPeriod(now, period).getTime(),
    past: startOfRollupPeriod(s.value, period).getTime() <= now.getTime(),
    partial:
      endOfRollupPeriod(s.value, period).getTime() > now.getTime() &&
      startOfRollupPeriod(s.value, period).getTime() < now.getTime(),
    incomplete:
      endOfRollupPeriod(s.value, period).getTime() > now.getTime() ||
      (index === 0 &&
        startOfRollupPeriod(value[0].value, period).getTime() ===
          startOfRollupPeriod(s.value, period).getTime()),
    ...(retirementInfo?.birthdate && retirementInfo?.targetRetirementAge
      ? {
          retired: isRetired(
            startOfRollupPeriod(retirementInfo?.birthdate, period),
            startOfRollupPeriod(s.value, period),
            retirementInfo?.targetRetirementAge
          ),
        }
      : {}),
  }));
};

export const segmentKeyForDate = (date, period, offset = 0) => {
  const year = date.getFullYear();
  const month = (date.getMonth() + offset) % 12;
  const quarter = month <= 2 ? 'Q1' : month <= 5 ? 'Q2' : month <= 8 ? 'Q3' : 'Q4';

  switch (period) {
    case 'month':
      return dateFns.format(date, 'MMM yy');

    case 'quarter':
      return `${quarter} ${year}`;

    case 'year':
      return dateFns.format(date, 'yyyy');

    default:
      return null;
  }
};

export const segmentForTransaction = (tx, dates) => {
  const txDate = dateFns.parseISO(tx.date);
  return segmentForDate('month', txDate.getTime(), dates);
};

export const segmentForDate = (period, txDate, dates) => {
  for (let i = 0; i < dates?.length; i++) {
    const segmentStartDate = new Date(dates[i]);

    if (i < dates.length) {
      // If this is not the last segment, derive the end date from the next segment
      const nextSegmentStartDate = endOfRollupPeriod(segmentStartDate, period).getTime();
      if (txDate >= segmentStartDate && txDate < nextSegmentStartDate) {
        return i;
      }
    }
  }

  // If the transaction date doesn't match any segment, you can handle it accordingly.
  return -1; // or throw an error, return a default segment, etc.
};

export const segmentContainsDate = (period, segment, date) =>
  startOfRollupPeriod(segment.value, period).getTime() ===
  startOfRollupPeriod(date, period).getTime();

export function countSegmentsInDateRange(start, end, segmentType) {
  if (!start || !end) return 0;
  const startDate = startOfRollupPeriod(start, segmentType);
  const endDate = startOfRollupPeriod(end, segmentType);
  // Validate input and ensure start date is before or equal to end date
  if (!(startDate instanceof Date) || !(endDate instanceof Date) || startDate > endDate) {
    // throw new Error('Invalid input or start date is after end date');
    return 0;
  }

  // Calculate the number of segments based on the segment type
  let result = 0;
  switch (segmentType) {
    case 'month':
      result = dateFns.differenceInMonths(startDate, endDate); // +1 to include both start and end months
      break;
    case 'quarter':
      result = dateFns.differenceInQuarters(startDate, endDate); // +1 to include both start and end quarters
      break;
    case 'year':
      result = dateFns.differenceInYears(startDate, endDate); // +1 to include both start and end years
      break;
    default:
      throw new Error("Invalid segment type. Use 'month', 'quarter', or 'year'.");
  }

  return Math.abs(result) + 1;
}

export function excludeRefunds(_transactions) {
  const transactions = _transactions.filter((v) => v);

  const nonRefundTransactions = [];
  const processedTransactions = new Set();

  transactions?.forEach((tx) => {
    // Check if the transaction has been processed as a refund or has the category "Transfers"
    if (tx && !processedTransactions.has(tx) && tx.category !== 'Transfers') {
      const potentialRefunds = transactions.filter(
        (otherTx) =>
          tx !== otherTx && // Avoid comparing a transaction with itself
          tx.amount === -otherTx.amount &&
          tx.merchantName === otherTx.merchantName
      );

      if (potentialRefunds.length === 0) {
        // This is not a refund or "Transfers" category, add it to the list
        nonRefundTransactions.push(tx);
      } else {
        // Mark potential refunds as processed
        potentialRefunds.forEach((refundTx) => processedTransactions.add(refundTx));
      }
    }
  });

  return nonRefundTransactions;
}

// Determine the divisor for the rollup period
export function divisorForPeriod(p) {
  switch (p) {
    case 'year':
      return 1;
    case 'quarter':
      return 3;
    case 'month':
      return 12;
    default:
      return 1; // Default to annual (no scaling)
  }
}

// Step size for period representing the steps between calculating inflation
// i.e. calculate inflation on a monthly basis
export const stepSizeForPeriod = (period) => {
  let stepSize = 1;

  switch (period) {
    case 'month':
      stepSize = 1;
      break;
    case 'quarter':
      stepSize = 3;
      break;
    case 'year':
      stepSize = 12;
      break;
    case 'none':
    default:
      stepSize = 0;
      break;
  }

  return stepSize;
};

export const multiplierForPeriod = stepSizeForPeriod;

// convert data from monthly, to quarter or yearly
export function rollUpData(fromSegments, toSegments, rollup, period) {
  const rolledUpData = {};

  // Create a mapping of fromSegment labels to their corresponding toSegment labels
  const segmentMapping = {};
  fromSegments.forEach((fromSegment) => {
    const matchingToSegment = toSegments.find(({ value }) =>
      segmentContainsDate(period, fromSegment, new Date(value))
    );
    if (matchingToSegment) {
      segmentMapping[fromSegment.label] = matchingToSegment.label;
    }
  });

  // Iterate over the data categories
  Object.entries(rollup).forEach(([category, entry]) => {
    const { data } = entry;
    const baseData = {};

    // Initialize the baseData with zeros for each toSegment
    toSegments.forEach(({ label }) => {
      baseData[label] = 0;
    });

    // Iterate over the fromSegments and distribute values to the corresponding toSegments
    fromSegments.forEach((fromSegment) => {
      const fromLabel = fromSegment.label;

      // Check if the fromSegment exists in the category's data
      if (data[fromLabel] !== undefined) {
        // Get the corresponding toSegment label from the mapping
        const toLabel = segmentMapping[fromLabel];

        // Update the value in the baseData for the matching toSegment
        if (toLabel) {
          baseData[toLabel] += data[fromLabel];
        }
      }
    });

    // Add the new category to the rolledUpData
    rolledUpData[category] = { data: baseData };
  });

  // Return the formatted rolled up data
  return rolledUpData;
}

export function sumArrayObjectsByCategory(data, key = 'primary') {
  return Object.values(
    data.reduce((result, currentItem) => {
      const { name, data: dataArray } = currentItem;
      const primaryKey = categoryMap(name)?.[key];

      if (primaryKey) {
        if (!result[primaryKey]) {
          result[primaryKey] = { name: primaryKey, data: dataArray };
        } else {
          result[primaryKey].data = result[primaryKey]?.data.map(
            (value, index) => value + dataArray[index]
          );
        }
      }

      return result;
    }, {})
  );
}

export function calculateValueBySegment(rollup) {
  // Calculate average income for each segment using a reducer
  const valueBySegment = {};

  Object.keys(rollup).forEach((segment) => {
    valueBySegment[segment] = Object.entries(rollup[segment]).reduce(
      (sum, [key, value]) => sum + value ?? 0,
      0
    );
  });

  // Calculate the average income in general (across all transactions)
  const averageValueBySegment = calculateAverageValue(valueBySegment);

  return { valueBySegment, averageValueBySegment };
}

function calculateAverageValue(rollup) {
  const segmentsLength = Object.keys(rollup).length;
  const transactions = Object.values(rollup);
  const totalIncome = transactions.reduce((sum, amount) => sum + amount, 0);
  return totalIncome / segmentsLength;
}

// export const mapRollup = (rollup) => {
//   const newCategories = {};

//   const detailedCategories = flattenObject(Object.values(rollup).map((e) => e.categories));

//   Object.keys(detailedCategories)?.forEach((key) => {
//     const data = detailedCategories[key];
//     const domainCategory = categoryMap(key)?.primary; // display name of domain category

//     if (!newCategories[domainCategory]) {
//       newCategories[domainCategory] = { categories: { [key]: data } };
//     } else {
//       const prev = newCategories[domainCategory];
//       newCategories[domainCategory] = { categories: { ...prev.categories, [key]: data } };
//     }
//   });

//   return newCategories;
// };
